import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/nextjs'
import Markdown from 'markdown-to-jsx'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import CheckboxField from '@/legacy-src/components/formFields/CheckboxField'
import InputField from '@/legacy-src/components/formFields/InputField'
import Icon from '@/legacy-src/components/icons/Icon'
import LoginButtons from '@/legacy-src/components/loginButtons'
import { RegisterFormFields, type RegisterFormValues } from '@/legacy-src/components/register/types'
import { Button } from '@/legacy-src/components/ui/button'
import WarningBox from '@/legacy-src/components/WarningBox'
import { useApis } from '@/legacy-src/contexts/api-context'
import { generalErrorHandler } from '@/legacy-src/epics/EpicHelpers'
import UserActions from '@/legacy-src/redux/UserRedux'
import { getAuthPageUrl, getCompanyEmployeeFromQuery, isCompanyEmployeeInQuery } from '@/legacy-src/utilities/authUtils'
import { getTranslate } from '@/legacy-src/utilities/i18n'
import { setTokens } from '@/legacy-src/utilities/setTokens'

import getGACookie from '../../analytics/getGaCookie'
import { useUserIp } from '../../contexts/user-ip-context'
import Toaster from '../../utilities/toaster'
import { validationRegisterSchema } from './validationRegisterSchema'

interface RegisterFormProps {
  therapistId?: number | null
  isDiagnosis?: boolean
  gtmEventData?: Record<string, any>
}

const RegisterForm = ({ therapistId = null, isDiagnosis, gtmEventData }: RegisterFormProps) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const { userIp } = useUserIp()
  const { t } = getTranslate()
  const { next, api } = useApis()

  const [isEmailLoginDisplayed, setIsEmailLoginDisplayed] = useState(false)
  const [isNonActivatedHearmeUser, setIsNonActivatedHearmeUser] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const { query } = router

  const form = useForm<RegisterFormValues>({
    reValidateMode: 'onBlur',
    defaultValues: {
      [RegisterFormFields.gdpr]: false,
      [RegisterFormFields.email]: '',
      [RegisterFormFields.phone]: '',
      [RegisterFormFields.password]: '',
    },
    // Only for CI type check TS config
    // @ts-expect-error Issue related to stricNullChecks and Zod schema inference
    resolver: zodResolver(validationRegisterSchema),
  })

  const handleSubmitForm = async (data: RegisterFormValues) => {
    const { email, password, gdpr, phone } = data

    const registrationIp = userIp
    const registrationCookie = await getGACookie()

    try {
      setIsPending(true)
      const response = await next.register({
        email,
        password,
        gdpr,
        phone,
        consent: true,
        registrationIp,
        registrationCookie,
        ...(isCompanyEmployeeInQuery(query) ? { companyEmployee: getCompanyEmployeeFromQuery(query) } : {}),
      })
      Sentry.setUser({ email: data.email })
      Toaster.success('component.toaster.registrationSuccess')
      setTokens({
        api: api,
        data: { refreshToken: response.data.refreshToken, accessToken: response.data.accessToken },
      })
      dispatch(
        UserActions.getUserDataRequest(
          therapistId,
          isDiagnosis,
          {
            event: 'registrationFinished',
            destination: isDiagnosis ? 'diagnoseView' : 'dashboardView',
            ...gtmEventData,
          },
          undefined,
          router.query.redirectUrl,
        ),
      )
    } catch (error: any) {
      const resError = error?.response?.data
      if (resError?.type === 'NOT_ACTIVATED') {
        setIsNonActivatedHearmeUser(true)
      } else {
        generalErrorHandler('authorization')(error)
      }
    } finally {
      setIsPending(false)
    }
  }

  const handleSwitchToLogin = () => router.push(getAuthPageUrl('login', router.query))

  // Only for CI type check TS config
  // @ts-expect-error Issue related to stricNullChecks and Zod schema inference
  const handleSubmit = form.handleSubmit(handleSubmitForm)

  return (
    <>
      <LoginButtons buttonText={t('login.register.buttonText')} isDiagnosis={isDiagnosis} emphasizeSocials />

      {!isEmailLoginDisplayed ? (
        <div className="w-full text-center">
          <Button
            size="medium"
            className="word mx-auto my-6 whitespace-break-spaces"
            onClick={() => setIsEmailLoginDisplayed(true)}
          >
            {t(`registration.email.signUp`)}
          </Button>
        </div>
      ) : (
        <FormProvider {...form}>
          <form onSubmit={handleSubmit}>
            <InputField name={RegisterFormFields.email} label={t(`registration.email.label`)} required />
            <InputField
              name={RegisterFormFields.password}
              label={t(`registration.password.label`)}
              required
              type="password"
              showEye
            />

            <InputField name={RegisterFormFields.phone} type="tel" label={t(`registration.phone.label`)} optional />
            <div className="my-3 flex rounded-md bg-body-bg px-3 py-2 text-sm text-secondary">
              <Icon icon="customer-support" size="sm" color="primary" className="mr-2" />
              <Markdown>{t(`registration.phone.note`)}</Markdown>
            </div>
            <div className="my-8">
              <CheckboxField
                label={t(`registration.gdpr`)}
                name={RegisterFormFields.gdpr}
                className="flex flex-col !items-start"
              />
            </div>

            {isNonActivatedHearmeUser && <WarningBox text={t('registration.notActivated', { url: '/reset' })} />}

            <div className="my-8 flex justify-center">
              <Button onClick={handleSubmit} loading={isPending} className="whitespace-break-spaces">
                {t(`registration.email.signUp`)}
              </Button>
            </div>
          </form>
        </FormProvider>
      )}

      <div className="flex justify-center text-base">
        <span>{t(`registration.login.buttonLabel`)}</span>&nbsp;
        <div onClick={handleSwitchToLogin} className="cursor-pointer font-bold text-primary">
          {t(`component.button.login`)}
        </div>
      </div>
    </>
  )
}

export default RegisterForm
